'use strict';

const ALL_ORGS_LOCAL_STORAGE_KEY = 'Organizations-All';
const CURRENT_ORG_LOCAL_STORAGE_KEY = 'Organizations-Current';

export default angular
  .module('main.dashboard.dataProvider', [])
  .service('DashboardDataProvider', function($q,
                                             $rootScope,
                                             utils,
                                             UsersService,
                                             Resource,
                                             PermissionsService,
                                             ReportsService
  ) {
    const now = moment();
    const endOfDay = moment(now).endOf('day');

    class DashboardDataProvider {

      // #region Public fields

      /**
       * End of current day in GMT timezone. Use this value as ground zero for date calculations.
       * @return {Date}
       */
      get now() {
        return now.toDate();
      }

      // #region Resources

      /**
       * Available organizations
       * @type {Resource}
       */
      availableOrganizationsResource = new Resource(
        () => {
          // TODO move get all orgs stuff to the OrganizationService
          return utils.getStorageItem(ALL_ORGS_LOCAL_STORAGE_KEY);
        }
      );

      /**
       * Current organization
       * @type {Resource}
       */
      currentOrganizationResource = new Resource(
        () => {
          // TODO move get/set current org stuff to the OrganizationService
          return utils.getStorageItem(CURRENT_ORG_LOCAL_STORAGE_KEY);
        }
      );

      /**
       * Current organization ID
       * @type {Resource}
       */
      currentOrganizationIdResource = new Resource(
        () => this.currentOrganizationResource.value.id,
        [
          this.currentOrganizationResource
        ]
      );

      /**
       * Current user (admin currently logged in)
       * @type {Resource}
       */
      currentUserResource = new Resource(
        () => UsersService.getCurrentUserInfo()
      );

      /**
       * List of all report groups available for current organizations
       * @type {Resource}
       */
      reportGroupsResource = new Resource(() => {
        return ReportsService.getReportGroups(this.currentOrganizationIdResource.value)
          .then(data => data?.groups || []);
      }, [
        this.currentOrganizationIdResource
      ]);

      /**
       * Report group for organization dashboard
       * @type {Resource}
       */
      dashboardReportGroupResource = new Resource(() => {
        return this.reportGroupsResource.value.find(reportGroup => !!reportGroup['dashboard']);
      }, [
        this.reportGroupsResource
      ]);

      /**
       * Report group ID for organization dashboard
       * @type {Resource}
       */
      dashboardReportGroupIdResource = new Resource(() => {
        return this.dashboardReportGroupResource.value?.id;
      }, [
        this.dashboardReportGroupResource
      ]);

      // /**
      //  * Device types
      //  * @type {Resource}
      //  */
      // deviceTypesResource = new Resource(() => {
      //   return DevicesService.getDeviceTypes()
      //     .then((data) => data?.deviceTypes || []);
      // }, []);

      // #endregion

      isUnlimitedAdmin = PermissionsService.isUnlimitedAdmin();
      isPaidAdmin = PermissionsService.isPaidAdmin();
      isServiceAdmin = PermissionsService.isServiceAdmin();

      // #endregion

      // #region Private fields

      // #endregion

      constructor() {
        $rootScope.$on('chooseOrganizationEvent', ($event, org, menuName) => {
          this.currentOrganizationResource.value = org;
          this.currentOrganizationResource.forceReInitDependentsRecursively();
        });
      }

      // #region Public methods

      /**
       * Destroy current Data Provider. Obviously, you can not use this object after destruction
       */
      destroy() {
        // destroy every Resource and Resource Group in current object
        for (let [key, value] of Object.entries(this)) {
          if (value && value.isResource) {
            value.destroy();
            delete this[key];
          }
        }
      }

      /**
       * Get report history data for dates specified
       * @param {number} reportId
       * @param {number} startDate
       * @param {number} endDate
       * @return {Promise}
       */
      getReportData(reportId, startDate, endDate) {
        return $q.all([
          this.dashboardReportGroupIdResource.init(),
          this.currentOrganizationIdResource.init()
        ])
          .then(() => ReportsService.getReportExecutions({
            reportId: reportId,
            reportGroupId: this.dashboardReportGroupIdResource.value,
            organizationId: this.currentOrganizationIdResource.value,
            startDate: startDate,
            endDate: endDate || endOfDay.valueOf()
          }));
      }

      /**
       * Subscriptions distribution across all locations within selected Organization.
       *   Definition: For multiple subscriptions at single location count only highest one
       *   (highest first: "Pro", "Basic", "Trial", "No subscription").
       *   Count only locations with at least one user.
       * @param {number} startDate start date in UNIX epoch ms
       * @param {number} endDate end date in UNIX epoch ms
       * @return {Promise}
       */
      getSubscriptions(startDate, endDate) {
        return this.getReportData(104, startDate, endDate);
      }

      getSubscriptionsLastWeek() {
        return this.getSubscriptions(moment(now).subtract(1, 'w').valueOf(), endOfDay.valueOf());
      }

      /**
       * Total locations across current Organization.
       *   Definition: Locations with at least one user (we don't need to count locations without users,
       *   as they are not accessible by end-users)
       * @param startDate
       * @param endDate
       * @return {Promise}
       */
      getLocations(startDate, endDate) {
        return this.getReportData(105, startDate, endDate);
      }

      getLocationsTotalLastWeek() {
        return this.getLocations(moment(now).subtract(1, 'w').valueOf(), endOfDay.valueOf());
      }

      /**
       * Total unique users across all locations within selected Organization.
       * Contains following data series (number is `id` field from dictionary):
       *  1. Total Users - Daily report on total unique users.
       *  2. New Users - Daily report on total unique users with registration date during past 24 hours.
       *  3. Daily Active Users (DAU) - User's who called at least one API with personal user's (not admin's) API_KEY
       *    type, during last 24 hours.
       *  4. Monthly Active Users (MAU) - Same report as #1 running once per month, activity for previous calendar month.
       * @param {number} startDate start date in UNIX epoch ms
       * @param {number} endDate end date in UNIX epoch ms
       * @return {Promise}
       */
      getUsers(startDate, endDate) {
        return this.getReportData(101, startDate, endDate);
      }

      getUsersLastWeek() {
        return this.getUsers(moment(now).subtract(1, 'w').valueOf(), endOfDay.valueOf());
      }

      /**
       * Emergency Call Center Alerts and Dispatched Emergency Services.
       * Total AvantGuard escalations (alerting professional call-center), and how many times emergency services
       *   dispatched for locations within selected Organization.
       * Collected from AvantGuard confirmation on the dispatch.
       * Multiple alerts within single location at the same date should be counted as well.
       * Counted for all locations within current Organisation.
       *
       * AvantGuard alerts across related locations, every dispatch across related locations.
       *
       * @param {number} startDate start date in UNIX epoch ms
       * @param {number} endDate end date in UNIX epoch ms
       * @return {Promise}
       */
      getCallCenterAlerts(startDate, endDate) {
        return this.getReportData(106, startDate, endDate);
      }

      getCallCenterAlertsLastWeek() {
        return this.getCallCenterAlerts(moment(now).subtract(1, 'w').valueOf(), endOfDay.valueOf());
      }

      /**
       * Total Devices - Connected and disconnected
       * Total devices of any types for the specified Organization
       * Not counted exceptions: iOS, Android Cameras, all GWs and software proxies
       * @param {number} startDate start date in UNIX epoch ms
       * @param {number} endDate end date in UNIX epoch ms
       * @return {Promise}
       */
      getTotalDevices(startDate, endDate) {
        return this.getReportData(109, startDate, endDate);
      }

      getTotalDevicesLastWeek() {
        return this.getTotalDevices(moment(now).subtract(1, 'w').valueOf(), endOfDay.valueOf());
      }

      /**
       * Total Gateways - Connected and disconnected
       * Total connected GWs of any types across Organization. Include disconnected GWs, or total GWs.
       * Count only GWs that sold with a packs (G1, Develco), do not include third-party and software GWs.
       * @param {number} startDate start date in UNIX epoch ms
       * @param {number} endDate end date in UNIX epoch ms
       * @return {Promise}
       */
      getTotalGateways(startDate, endDate) {
        return this.getReportData(107, startDate, endDate);
      }

      getTotalGatewaysLastWeek() {
        return this.getTotalGateways(moment(now).subtract(1, 'w').valueOf(), endOfDay.valueOf());
      }

      /**
       * Gateway Connectivity - Ethernet, Wifi or Cellular
       * How many GWs connected via different network connections.
       *
       * Count only GWs that sold with a packs (G1, Develco), do not include third-party and software GWs.
       *
       * @param {number} startDate start date in UNIX epoch ms
       * @param {number} endDate end date in UNIX epoch ms
       * @return {Promise}
       */
      getGatewayConnectivity(startDate, endDate) {
        return this.getReportData(108, startDate, endDate);
      }

      getGatewayConnectivityLastWeek() {
        return this.getGatewayConnectivity(moment(now).subtract(1, 'w').valueOf(), endOfDay.valueOf());
      }

      // #endregion

      // #region Private methods

      /**
       * Returns trend out of executions data
       * @param {Array<{}>} data Executions data
       * @param {string} propName Property name to look for
       * @return {number}
       */
      calculateTrend(data, propName) {
        if (!data || !data.length || data.length < 1) {
          return undefined;
        }
        if (data[data.length - 1][propName] == null || data[0][propName] == null) {
          return undefined;
        }
        if (data[0][propName] === 0) {
          return data[data.length - 1][propName] === 0 ? 0 : 100;
        }
        return 100 * (data[data.length - 1][propName] - data[0][propName]) / data[0][propName];
      }

      // #endregion

    }

    return new DashboardDataProvider();
  })
  .name;
